import { CONST } from '@common/constants/const.constant';
import { IBEError } from '@common/interfaces/be-error.interface';
import { ICollection } from '@common/interfaces/collection.interface';
import { UseQueryOptions } from '@common/interfaces/query-options.interface';
import { createCollection } from '@common/utils/create-collection.util';
import { customNotify, IErrorItem } from '@common/utils/custom-notify.util';
import { createSalesAddonDto, ISalesAddonDto } from '@src/api/model/sales-addon.dto';
import {
    ISubscriptionSCsForSale,
    packHouseApiRequest,
    TPackhouseV1SalesConfigByDeliveryIdIdGetError,
} from '@src/api/pack-house-generated-api';
import { queryClient } from '@src/shared/query-client';
import { useEffect, useMemo } from 'react';
import { useQuery, UseQueryResult } from 'react-query';
import { apiHooks } from '../api-hooks';

const NAME = 'sales-addon';

interface IProps {
    deliveryConfigId: string;
    sales_availability_to: number;
}

const refetchSalesAddons = (deliveryConfigId: string): void => {
    queryClient.refetchQueries([`${NAME}-${deliveryConfigId}-list`]);
};

const useGetSalesAddons = (
    arg: IProps,
    queryOptions?: UseQueryOptions,
): UseQueryResult<ICollection<ISalesAddonDto>, IBEError<TPackhouseV1SalesConfigByDeliveryIdIdGetError>> => {
    const queries = [];
    const productQuery = apiHooks.packHouse.useGetProducts();
    queries.push(productQuery);

    const QUERY_KEY = useMemo(() => [`${NAME}-${arg.deliveryConfigId}-list`, arg], [arg]);
    const result = useQuery<{ temp?: ISubscriptionSCsForSale }, IBEError<TPackhouseV1SalesConfigByDeliveryIdIdGetError>>(
        [`${NAME}-${arg.deliveryConfigId}-list`, arg],
        () => {
            return packHouseApiRequest
                .packhouseV1SalesConfigByDeliveryIdIdGet(arg.deliveryConfigId, {
                    sales_availability_to: arg.sales_availability_to,
                })
                .then((res) => {
                    return { temp: res?.data };
                });
        },
        {
            ...queryOptions,
        },
    );

    queries.push(result);

    const isSuccess = queries.every((q) => q.isSuccess);
    const prev = queryClient.getQueryData(QUERY_KEY) as ICollection<ISalesAddonDto> | undefined;
    const data: ICollection<ISalesAddonDto> | undefined = useMemo(() => {
        if (isSuccess) {
            if (result.data?.temp) {
                const res = createCollection(
                    result.data.temp?.list
                        ?.map((addon) => createSalesAddonDto(addon)),
                    0,
                    { limit: CONST.LIST_LIMIT },
                );
                queryClient.setQueryData(QUERY_KEY, res);
                return res;
            }
            return prev;
        }
        return undefined;
    }, [isSuccess, result.data?.temp, prev, QUERY_KEY]);

    const errors = queries.map((q) => ({ beError: q.error, onShown: q.remove } as IErrorItem));
    useEffect(() => {
        customNotify.error(errors);
    }, [errors]);

    return { ...result, isFetching: queries.some((q) => q.isFetching || q.isLoading), data } as unknown as ReturnType<
        typeof useGetSalesAddons
    >;
};

export const salesAddons = {
    refetchSalesAddons,
    useGetSalesAddons,
};
